import React from 'react';

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import { isEmail } from '@common/utils/email';
import { getCommonFieldBasedElementParams, useDatasheetElementController } from '@modules/datasheets/components/result/controllers/datasheet-element';

export type HyperlinkFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto>;

export const useHyperlinkFieldController = (props: HyperlinkFieldProps) => {
  const { datasheetElement, onChange } = props;

  enum Mode {
    editing = 'editing',
    viewing = 'viewing',
  };

  // const normalizedValue = datasheetElement.original.field.value || '';
  const normalizedValue = '';
  const [fieldSavedValue, setFieldSavedValue] = React.useState(normalizedValue);
  const [fieldCurrentValue, setCurrentFieldValue] = React.useState(normalizedValue);
  const [isLinkOpen, setLinkOpenState] = React.useState(false);
  const [mode, setMode] = React.useState(Mode.viewing);
  const [linkCoordinates, setLinkCoordinates] = React.useState({ top: 0, left: 0 });
  const inputRef = React.useRef<HTMLDivElement>();

  const { 
    headingSizeDependencies, 
    headingPlacedHorizontally, 
    headingPosition 
  } = getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(
    props,
    { headingSizeDependencies }
  );

  React.useEffect(() => {
    if (fieldSavedValue !== normalizedValue) {
      setFieldSavedValue(normalizedValue);
      setCurrentFieldValue(normalizedValue);
    }
  }, [normalizedValue]);

  const emailValid = React.useMemo(() => {
    return isEmail(fieldSavedValue);
  }, [fieldSavedValue]);

  const linkProps = React.useMemo(() => {
    if (emailValid) {
      return {
        href: `mailto:${fieldSavedValue}`
      };
    }

    return {
      href: fieldSavedValue,
      target: '_blank',
      rel: 'noopener noreferrer'
    };
  }, [fieldSavedValue, emailValid]);
  
  const handleFieldValueChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();

    const { value } = e.target;

    setCurrentFieldValue(value);
  }, []);

  const handleFieldBlur = React.useCallback(async () => {
    setMode(Mode.viewing);

    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        field: {
          ...datasheetElement.original.field,
          name: fieldCurrentValue,
        },
      },
    });

    setFieldSavedValue(fieldCurrentValue);
  }, [fieldCurrentValue, fieldSavedValue, onChange]);

  const closeLink = React.useCallback(() => {
    setLinkOpenState(false);
  }, []);

  const openLink = React.useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    const { bottom, left } = inputRef.current.getBoundingClientRect();

    setLinkCoordinates({ left, top: bottom - 2 });
    setLinkOpenState(true);
  }, []);

  const handleInputMouseEnter = React.useCallback(() => {
    if (mode !== Mode.editing) {
      openLink();
    }
  }, [mode, openLink]);

  const handleFieldClick = React.useCallback(() => {
    closeLink();
    setMode(Mode.editing);
  }, [closeLink]);

  return {
    headingPlacedHorizontally, 
    headingPosition,
    handleInputMouseEnter,
    closeLink,
    inputRef,
    fieldCurrentValue,
    handleFieldValueChange,
    handleFieldBlur,
    handleFieldClick,
    isLinkOpen,
    linkCoordinates,
    fieldSavedValue,
    linkProps,
    emailValid,
    ...baseDatasheetElementController,
  };
};